import * as React from 'react';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * KpiSourcePill — "every metric cites its origin."
 *
 * README §6 (load-bearing UI primitives): "This is the IV&V trust contract,
 * not a stylistic choice." A small document-icon-prefixed pill rendered
 * directly under every numeric metric in the hero facts strip and scorecard
 * tiles. The source string is free-form attribution (e.g.,
 * "SOW v2 §3.6 · CO-04") — the icon makes the citation visually unmissable
 * without spending a chip pill's height on it.
 *
 * Visual contract (matches prototype tokens.css `.kpi-source` rule):
 *   - paper-100 background, ink-100 border, 4px radius
 *   - 11px ink-700 text, 5px gap between icon and text
 *   - lucide `FileText` icon at 11px sized to ink-500
 *   - source text truncates with an ellipsis when it overflows the
 *     containing tile (truncation is the responsibility of the parent's
 *     `min-w-0` flex constraint; the inner span carries the
 *     overflow-hidden + text-overflow rules)
 *
 * Translation note: the prototype renders the icon via an SVG `<use>` against
 * the Feather sprite (`ui.js icon('i-file-text', 11)`). We use lucide-react's
 * `FileText` here because it ships with the app and renders pixel-equivalent
 * to Feather at this size.
 */
export interface KpiSourcePillProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Source attribution string, e.g. "SOW v2 §3.6 · CO-04". */
  source: string;
}

const KpiSourcePill = React.forwardRef<HTMLDivElement, KpiSourcePillProps>(
  ({ source, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'mt-2 inline-flex min-w-0 max-w-full items-center gap-[5px] rounded px-2 py-1',
        'bg-paper-100 border border-ink-100',
        'text-[11px] leading-[1.3] text-ink-700',
        className,
      )}
      {...props}
    >
      <FileText className="size-[11px] shrink-0 text-ink-500" aria-hidden="true" />
      <span className="truncate font-medium">{source}</span>
    </div>
  ),
);
KpiSourcePill.displayName = 'KpiSourcePill';

export { KpiSourcePill };
